<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in filteredRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 mx-6">
                <VaIcon
                v-if="childRoute.meta?.icon"
                aria-hidden="true"
                :name="childRoute.meta?.icon"
                size="15px"
                :color="iconColor(childRoute)"
              />
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
    <div class="p-4">
      <VaButton
      gradient
        color="primary"
        class="w-full py-1"
        :aria-label="t('sidebar.buyLicense')"
        @click="handleBuyLicenseClick"
      >
        {{ t('sidebar.buyLicense') }}
      </VaButton>
    </div>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useUserStore } from '@/stores/user.store'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const userStore = useUserStore()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    // Filter routes based on user permissions
    const filteredRoutes = computed(() => {
      return navigationRoutes.routes.filter(route => {
        // If route name is 'admin-panel', only show to admin users
        if (route.name === 'admin-panel') {
          return userStore.isAdmin
        }
        // Show all other routes
        return true
      })
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.name?.toString()?.includes(section.name) || false
      }
      return section.children.some(childRoute => route.name?.toString()?.includes(childRoute.name)) || false
    }

    const setActiveExpand = () => {
      value.value = filteredRoutes.value.map((route: INavigationRoute) => {
        // Keep admin panel expanded if user is admin
        if (route.name === 'admin-panel' && userStore.isAdmin) {
          return true
        }
        return routeHasActiveChild(route)
      })
    }

    // Watch for user store changes to update expansion state
    watch(() => userStore.isAdmin, (newValue) => {
      if (newValue) {
        setActiveExpand()
      }
    }, { immediate: true })

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    const handleBuyLicenseClick = () => {
        router.push({ name: 'pricing-plans' })
    }

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      filteredRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      handleBuyLicenseClick,
    }
  },
})
</script>
